import { type IPrincipleMention } from '../mention/mention';
import { type ITemplateDefinition } from './template-definition';
import { type TemplateScope } from './template-scope';

export interface ITemplateScopeData {
  [key: string]: unknown;
}

/**
 * The data used to populate a template body.
 * All properties must be a string | number
 */
export interface ITemplateContext {
  [key: string]: string | number | boolean;
}

export interface IResolvedTemplateContext {
  context: ITemplateContext;
  scopeData: ITemplateScopeData;
}

export interface ITemplateContextOption {
  label: string;
  scope: TemplateScope;
  /** Lazily builds context + scope data; mints portal-link tokens on first call.
   *  Memoises the PROMISE so concurrent calls dedupe. May resolve undefined
   *  when the underlying scope data cannot be built (e.g. gap candidate gone). */
  resolve(): Promise<IResolvedTemplateContext | undefined>;
}

/**
 * Builds an {@link ITemplateContextOption} for callers that already have the
 * resolved context + scope data eagerly built (e.g. EmptyContextProvider, the
 * referral builder). The returned `resolve()` is a no-op that resolves
 * immediately to the supplied value, satisfying the lazy interface without
 * minting anything.
 */
export function resolvedContextOption(
  label: string,
  scope: TemplateScope,
  resolved: IResolvedTemplateContext
): ITemplateContextOption {
  return {
    label,
    scope,
    resolve: () => Promise.resolve(resolved),
  };
}

/**
 * Denotes the ability to create a template context
 */
export interface IContextBuilder<TemplateContext extends ITemplateContext> {
  build(): TemplateContext;
}

export type WithTemplates<T> = T & ITemplatesWithContext;
export type WithTemplateResource<T> = T & {
  templateResource: IPrincipleMention;
};

export interface ITemplatesWithContext {
  templates: ITemplateDefinition[];
  contextOptions: ITemplateContextOption[];
}

export enum SystemTemplate {
  PrintHeader = 'printHeader',
  PrintFooter = 'printFooter',
  PrintInvoiceHeader = 'printInvoiceHeader',
  PrintInvoiceFooter = 'printInvoiceFooter',
  PrintTreatmentPlanHeader = 'printTreatmentPlanHeader',
  PrintTreatmentPlanFooter = 'printTreatmentPlanFooter',
  InvoiceSMS = 'invoiceSMS',
  InvoiceEmail = 'invoiceEmail',
  GapOfferSMS = 'gapOfferSMS',
  GapOfferEmail = 'gapOfferEmail',
  DepositRequestSMS = 'depositRequestSMS',
  DepositRequestEmail = 'depositRequestEmail',
  DepositConfirmationEmail = 'depositConfirmationEmail',
  DepositReminderEmail = 'depositReminderEmail',
  DepositForfeitNoticeEmail = 'depositForfeitNoticeEmail',
  DepositRefundConfirmationEmail = 'depositRefundConfirmationEmail',
}
